import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import ToggleSwitch from 'react-switch';
import { useHistory } from 'react-router-dom';

import { blackberry, coolGrey, grape, greyStroke, squidInk, white } from 'lib/css/colors';
import { PRODUCT_LISTS } from 'lib/networking/endpoints';
import usePost from 'lib/hooks/usePost';

function CreateProductListModal({ show, onHide, refetch }) {
  const [name, setName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const history = useHistory();

  const onClose = () => {
    setName('');
    setIsPrivate(false);
    onHide();
  };

  const { postData: createList, loading } = usePost(
    PRODUCT_LISTS,
    response => {
      const prodList = response.data.product_list;
      Swal.fire({ title: 'Product list created successfully.', icon: 'success' }).then(() => {
        if (refetch) {
          refetch();
        }
        onClose();
        history.push('/product-lists/' + prodList.id);
      });
    },
    error =>
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        icon: 'error',
      }),
  );

  return (
    <Modal animation={false} centered show={show} onHide={onClose} size="md">
      <div className={css(styles.modalContent)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>Create Product List</h2>
          <Button
            variant="link"
            className={css(styles.closeButton)}
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
        <Modal.Body className={css(styles.body)}>
          <Form>
            <Form.Group className={css(styles.formGroup)}>
              <Form.Label className={css(styles.label)}>List Name</Form.Label>
              <Form.Control
                type="text"
                className={css(styles.input)}
                value={name || ''}
                placeholder="e.g. Q4 Restaurant Coverage"
                onChange={e => setName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className={css(styles.formGroup)}>
              <Form.Label className={css(styles.label)}>Make this list private</Form.Label>
              <div>
                <ToggleSwitch
                  onColor={grape}
                  checked={isPrivate}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onChange={() => setIsPrivate(!isPrivate)}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <div className={css(styles.footer)}>
          <Button
            className={css(styles.primaryButton)}
            disabled={!name || loading}
            onClick={() => createList({ name, is_private: isPrivate })}
          >
            {loading ? <Spinner animation="border" role="status" size="sm" /> : 'Create List'}
          </Button>
          <Button className={css(styles.cancelButton)} disabled={loading} onClick={() => onClose()}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${greyStroke}`,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: squidInk,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: coolGrey,
    fontSize: 18,
    cursor: 'pointer',
    padding: 8,
    borderRadius: 6,
    ':hover': {
      backgroundColor: `${grape}15`,
      color: grape,
    },
  },
  body: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    color: squidInk,
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    border: `1px solid ${greyStroke}`,
    padding: '10px 14px',
    ':focus': {
      borderColor: grape,
      boxShadow: `0 0 0 1px ${grape}`,
    },
  },
  footer: {
    display: 'flex',
    gap: 12,
    padding: '16px 24px',
    borderTop: `1px solid ${greyStroke}`,
    backgroundColor: white,
  },
  primaryButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: grape,
    },
    ':disabled': {
      backgroundColor: greyStroke,
      color: coolGrey,
    },
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${grape}`,
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: `${grape}15`,
    },
  },
});

CreateProductListModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  refetch: PropTypes.func,
};

export default CreateProductListModal;
